<!-- routify:options param-is-page=true -->
<script lang="ts">
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import ProductDetailsPage from "@retailer/svelte/ProductDetails/Page.svelte";
  import DiscountsLookupStore from "@retailer/svelte/stores/DiscountsLookupStore";
  import { isRestrictedRole } from "@retailer/services/preview_forbidden_flash";
  import ProductTypesStore from "@shared/svelte/Stores/ProductTypesStore.js";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import SfPricesStore from "@retailer/svelte/stores/SfPricesStore";
  import ProductsLastOrderedStore from "@retailer/svelte/stores/ProductsLastOrderedStore";
  import ProductLineLastOrderedStore from "@retailer/svelte/stores/ProductLineLastOrderedStore";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import ProductDetailPageRequestService from "@modern/services/retailer/product_detail_page_request_service";
  import { getDataForInventories } from "@retailer/services/pricing_data_service";
  import { params, goto, redirect, afterPageLoad } from "@roxi/routify";
  import { get as getStoreValue } from "svelte/store";
  import Spinner from "@shared/svelte/Components/SpinnerInternal.svelte";
  import FuturePricesStore from "@shared/svelte/Stores/FuturePricesStore.js";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import { getDistributors } from "@retailer/svelte/ProductDetails/Api.svelte";

  import { shouldUseRetailerFulfillmentMethodForDistributor } from "@retailer/services/fulfillment_methods_service";

  import Flash from "@shared/services/flashv2.js";
  import cart from "@modern/services/cart";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";

  import type { Distributor } from "@shared/types/Distributor";
  import type { SfProduct, VendorProduct } from "@shared/types/SevenFifty";
  import type { ProductLine, Product } from "@shared/types/ProductLine";
  import type { SalesRep } from "@shared/types/SalesRep";
  import type { PricingDataResult } from "@shared/types/Pricing";
  import type { DiscountResult } from "@retailer/services/discounts_service";

  export let productId: number;
  const pdpRequestService = new ProductDetailPageRequestService();
  const authService = getControlStateAuthenticationService();

  let firstPageLoadHasRun = false;
  $afterPageLoad(() => {
    //@ts-ignore
    if (stagedRolloutCheck("marketplace__poc_redirect") && window.RAILS_ENV !== "production") {
      window.location.href = "https://pdp-mfe.pvfog.org/products/" + $params.productId;
    }
    if (!firstPageLoadHasRun) {
      window.scrollTo(0, 0);
      firstPageLoadHasRun = true;
    }
    return true;
  });

  let distributorChoices: Distributor[] = [];

  const getInitialDistributorId = (): number | null => {
    if ($params.distributorId) return $params.distributorId;

    if (stagedRolloutCheck("dual_products")) {
      const integratedDistributors = distributorChoices.filter(d => {
        return d.integrated || (d.seven_fifty_integration && d.uses_price_zones);
      });
      if (integratedDistributors.length === 1) return integratedDistributors[0].id;
      if (distributorChoices.length === 1) return distributorChoices[0].id;
    }
  };

  const selectDistributor = (selectedDistributor: Distributor) => {
    if (selectedDistributor.id === distributor?.id) return;

    pricingDataReady = false;
    const { productId, ...restParams } = $params;
    $redirect(`/products/${productId}`, {
      ...restParams,
      distributorId: selectedDistributor.id,
    });

    distributor = selectedDistributor;
    reloadDistributorDependentData(selectedDistributor.id);
  };

  const Cart = cart.getCartService();

  let selectedVintage: string;
  $: selectedVintage = $params.vintage;

  let sevenFiftyProduct: SfProduct;
  const sevenFiftyProductRes = async productsData => {
    const vintage = selectedVintage === "Current" ? null : selectedVintage;
    const productWithSfProduct = productsData.find(
      p => p.inventory?.sf_product_id && (!vintage || p.vintage_year == vintage),
    );
    const productMatches =
      sevenFiftyProduct &&
      sevenFiftyProduct.product_id == productWithSfProduct?.inventory?.sf_product_id;
    if (!productWithSfProduct || productMatches) return;

    try {
      sevenFiftyProduct = await pdpRequestService.makeRequest({
        method: "GET",
        url: `/api/retailer/sf_products/${productWithSfProduct.inventory.sf_product_id}`,
      });
    } catch (e) {
      console.error("Error fetching SevenFiftyProduct", e);
    }
  };

  let vendorProductsByDistributorId: Record<number, VendorProduct> = {};
  const vendorProductRes = async (distributorData, productsData) => {
    const distributorId = distributorData?.id;
    const vintage = selectedVintage === "Current" ? null : selectedVintage;
    const productWithVendorProduct = productsData.find(
      p => p.inventory?.vendor_product_id && (!vintage || p.vintage_year == vintage),
    );

    if (
      distributorId &&
      vendorProductsByDistributorId[distributorId] &&
      vendorProductsByDistributorId[distributorId].vendor_product_id ==
        productWithVendorProduct?.inventory.vendor_product_id
    ) {
      return;
    }

    if (!distributorData?.seven_fifty_integration || !productWithVendorProduct) {
      return;
    }

    try {
      vendorProductsByDistributorId[distributorId] = await pdpRequestService.makeRequest({
        method: "GET",
        url: `/api/retailer/vendor_products/${productWithVendorProduct.inventory.vendor_product_id}`,
      });
    } catch (e) {
      console.error("Error fetching VendorProduct", e);
    }
  };

  $: if (selectedVintage) onSelectVintage();
  const onSelectVintage = () => {
    if (distributor && products) {
      pullSevenFiftyData(distributor, products);
    }
  };

  const stateSettingsRes = async () => {
    let res = await pdpRequestService.makeRequest({
      method: "GET",
      url: "/api/retailer/state_settings",
    });
    StateSettingsStore.set(res);
    return res;
  };

  const productTypes = getStoreValue(ProductTypesStore) || {};

  const productLinesRes = async distributorId => {
    let res = await pdpRequestService.makeRequest({
      method: "GET",
      url: "/api/retailer/product_lines/" + productId,
      data: {
        sspl_id: $params.ssplId,
        share_id: $params.shareId,
        distributor_id: distributorId,
      },
    });
    if (res == null) {
      $goto("/product_missing");
    }
    return res;
  };

  const loadDistributorChoices = async () => {
    await getDistributors(productId)
      .then((response: Distributor[]) => {
        if (response) {
          distributorChoices = response;
        }
      })
      .catch(error => {
        error.errors?.messages?.forEach((message: string) => {
          console.error(message);
        });
        Flash.now({
          type: "error",
          message: "Sorry, we've had a problem loading this product. Please contact support.",
        });
        distributorChoices = [];
      });
  };

  const allDistributorsRes = async () => {
    if (!getStoreValue(DistributorsStore).length) {
      let res = await pdpRequestService.makeRequest({
        method: "GET",
        url: "/api/retailer/distributors",
        data: {
          with_integrated: true,
          with_sales_reps: true,
          with_domains: true,
          with_cutoff_times: true,
        },
      });
      DistributorsStore.set(res);
      return res;
    }
    return {};
  };

  let retailerDistributor;
  let retailerDistributorLoaded = false;

  const retailerDistributorRes = async distributorData => {
    if (!distributorData?.id) return;

    const response = await pdpRequestService.makeRequest({
      url: "/api/retailer/retailer_distributors",
      method: "GET",
      data: {
        distributor_id: distributorData.id,
        limit: 1,
      },
    });

    if (response && response[0]) retailerDistributor = response[0];
    retailerDistributorLoaded = true;
  };

  let salesReps: SalesRep[] = [];
  let salesRepsLoaded = false;

  const salesRepsRes = async distributorData => {
    if (!distributorData?.id) return;
    if (isRestrictedRole(["supplier"])) return;

    const result = await pdpRequestService.makeRequest({
      method: "GET",
      url: `/api/retailer/distributors/${distributorData.id}/sales_reps`,
      data: { include_integrated_reps: true },
    });

    salesReps = result;
    salesRepsLoaded = true;
  };

  const productLineLastOrderedRes = async productLineId => {
    if (isRestrictedRole(["supplier", "sales_rep"])) return;
    if (!stagedRolloutCheck("dual_products")) return;

    if (!$ProductLineLastOrderedStore.hasDataForProductLine(productLineId)) {
      const result = await pdpRequestService.makeRequest({
        method: "GET",
        url: `/api/retailer/product_lines/${productLineId}/last_ordered`,
      });

      ProductLineLastOrderedStore.updateForProductLine(productLineId, result.last_ordered);
    }
  };

  const productsLastOrderedRes = async (distributorData, productsData) => {
    if (isRestrictedRole(["supplier", "sales_rep"])) return;
    if (!stagedRolloutCheck("show_last_order_on_pdp")) return;

    // Currently a PDP only shows the available products for one distributor at a time.
    // Sometimes there is no distributor (because there are none available, or because the retailer needs to pick one)
    // Sometimes there is a distributor, but that distributor has no available products
    // In either case, there's no reason to bother with this API call because there will be no products to show "last ordered" information for.
    // If the PDP ever changes to show products for multiple distributors at once, the assumptions here will have to change too.
    if (!distributor?.id || productsData.length === 0) return;

    const productIds = productsData.map(product => product.id);

    const result = await pdpRequestService.makeRequest({
      method: "GET",
      url: "/api/retailer/products_last_ordered",
      data: {
        distributor_ids: [distributorData.id],
        product_ids: productIds,
      },
    });

    const productsLastOrdered = result?.products_last_ordered || {};
    ProductsLastOrderedStore.setFromProductsLastOrdered(productsLastOrdered);
  };

  let pricingDataReady = false;
  const pullPricingData = async productsData => {
    pricingDataReady = false;
    const inventoryIds = productsData.map(p => p.inventory_id).filter(Boolean);
    const pricingData: PricingDataResult[] = await getDataForInventories(inventoryIds);
    if (pricingData.length && !isRestrictedRole(["supplier"])) {
      const discounts = pricingData.map(p => p.discounts).flat();
      DiscountsLookupStore.set(discounts as DiscountResult[]);
      const futurePrices = pricingData.reduce((arr, p) => {
        if (p.future_price) {
          arr.push(p.future_price);
        }
        return arr;
      }, []);
      FuturePricesStore.set(futurePrices);
      const sfPrices = pricingData.map(p => p.sf_prices).flat();
      SfPricesStore.setFromPrices(sfPrices);
    }
    pricingDataReady = true;
  };

  const pullAdditionalData = async (distributorData, productsData) => {
    productsLastOrderedRes(distributorData, productsData);
    retailerDistributorRes(distributorData);
    salesRepsRes(distributorData);

    if (
      shouldUseRetailerFulfillmentMethodForDistributor(
        distributorData?.id,
        distributorData?.uses_warehouse_pickup,
        stagedRolloutCheck("marketplace__fulfillment_methods") && distributorData?.uses_wine_cellar,
      )
    ) {
      await RetailerFulfillmentMethodsStore.updateDistributorFulfillmentMethod(distributorData?.id);
    }
  };

  const pullSevenFiftyData = (distributorData, productsData: Product[]) => {
    sevenFiftyProductRes(productsData);
    vendorProductRes(distributorData, productsData);
  };

  let productLine: ProductLine;
  let products: Product[];
  let distributor: Distributor;
  let loadFailed = false;

  const load = async () => {
    await loadDistributorChoices();
    const initialDistributorId = getInitialDistributorId();

    const promises = [
      productLinesRes(initialDistributorId),
      allDistributorsRes(),
      stateSettingsRes(),
    ];

    let results = await Promise.all(promises);

    productLine = results[0];

    if (!productLine) {
      loadFailed = true;
      return;
    }

    // This is a hack to make sure vintages don't display until a distributor is selected
    if (stagedRolloutCheck("dual_products") && productLine.dual_product && !initialDistributorId) {
      products = [];
    } else {
      products = productLine.products;
    }

    distributor = productLine.distributor as Distributor;

    pullPricingData(products);
    pullAdditionalData(distributor, products);
    pullSevenFiftyData(distributor, productLine.products);

    if (productLine.dual_product) {
      productLineLastOrderedRes(productLine.id);
    }
  };

  load();

  const reloadDistributorDependentData = async distributorId => {
    productLine = await productLinesRes(distributorId);
    if (!productLine) {
      loadFailed = true;
      return;
    }
    products = productLine.products;
    distributor = productLine.distributor as Distributor;

    pullPricingData(products);
    pullAdditionalData(distributor, products);
    pullSevenFiftyData(distributor, products);
  };

  const callbacks = {
    categorization: productTypes,
    extraTracking: {
      direct: !!$params.cid,
      cid: $params.cid,
    },
  };

  $: isLoading = !productLine || loadFailed;
  $: vendorProduct = distributor?.id ? vendorProductsByDistributorId[distributor.id] : null;
</script>

{#if isLoading}
  <Spinner />
{:else}
  <ProductDetailsPage
    {pricingDataReady}
    {callbacks}
    {Cart}
    {distributorChoices}
    {productLine}
    {products}
    {distributor}
    {retailerDistributor}
    {salesReps}
    accountDataLoaded={retailerDistributorLoaded && salesRepsLoaded}
    {selectDistributor}
    {sevenFiftyProduct}
    {vendorProduct}
  />
  {#if authService?.authenticationModalComponent}
    <svelte:component this={authService?.authenticationModalComponent} />
  {/if}
{/if}
